import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { quoteService } from '../services/quote-service';
import { Quote, CreateQuoteData, UpdateQuoteData, QuoteTag, QuoteType } from '../types/quote.types';

/**
 * Estado de cotizaciones
 */
interface QuoteState {
  // Estado
  quotes: Quote[];
  currentQuote: Quote | null;
  templates: Quote[];
  tags: QuoteTag[];
  quoteTypes: QuoteType[];
  isLoading: boolean;
  error: string | null;

  // Acciones - Quotes
  fetchQuotes: (quoteTypeId?: string) => Promise<void>;
  fetchQuoteTypes: () => Promise<void>;
  fetchQuote: (id: string) => Promise<void>;
  createQuote: (data: CreateQuoteData) => Promise<Quote>;
  saveDraft: (data: Partial<CreateQuoteData>) => Promise<Quote>;
  updateDraft: (id: string, data: Partial<CreateQuoteData>) => Promise<Quote>;
  updateQuote: (id: string, data: UpdateQuoteData) => Promise<Quote>;
  deleteQuote: (id: string) => Promise<void>;
  submitQuote: (id: string) => Promise<Quote>;
  setCurrentQuote: (quote: Quote | null) => void;
  clearError: () => void;

  // Acciones - Templates
  fetchTemplates: () => Promise<void>;
  saveAsTemplate: (id: string, templateName: string) => Promise<Quote>;
  createFromTemplate: (templateId: string) => Promise<Quote>;

  // Acciones - Tags
  fetchTags: () => Promise<void>;
  createTag: (name: string, color: string) => Promise<QuoteTag>;
  deleteTag: (id: string) => Promise<void>;
  setQuoteTags: (quoteId: string, tagIds: string[]) => Promise<void>;
  removeQuoteTag: (quoteId: string, tagId: string) => Promise<void>;
}

export const useQuoteStore = create<QuoteState>()(
  devtools(
    (set, _get) => ({
      // Estado inicial
      quotes: [],
      currentQuote: null,
      templates: [],
      tags: [],
      quoteTypes: [],
      isLoading: false,
      error: null,

      fetchQuoteTypes: async () => {
        try {
          const quoteTypes = await quoteService.getQuoteTypes();
          set({ quoteTypes });
        } catch (error: any) {
          console.error('Error al cargar tipos de cotización:', error);
        }
      },

      fetchQuotes: async (quoteTypeId?: string) => {
        try {
          set({ isLoading: true, error: null });
          let url = '/api/quotes';
          if (quoteTypeId) url += `?quoteTypeId=${quoteTypeId}`;
          const { apiClient } = await import('@/lib/api');
          const quotes = await apiClient.get<Quote[]>(url);
          set({ quotes, isLoading: false });
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al cargar cotizaciones',
            isLoading: false,
          });
          throw error;
        }
      },

      fetchQuote: async (id) => {
        try {
          set({ isLoading: true, error: null });
          const quote = await quoteService.getQuote(id);
          set({ currentQuote: quote, isLoading: false });
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al cargar cotización',
            isLoading: false,
          });
          throw error;
        }
      },

      createQuote: async (data) => {
        try {
          set({ isLoading: true, error: null });
          const quote = await quoteService.createQuote(data);
          set((state) => ({
            quotes: [...state.quotes, quote],
            currentQuote: quote,
            isLoading: false,
          }));
          return quote;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al crear cotización',
            isLoading: false,
          });
          throw error;
        }
      },

      saveDraft: async (data) => {
        try {
          set({ isLoading: true, error: null });
          const quote = await quoteService.saveDraft(data);
          set((state) => ({
            quotes: [...state.quotes, quote],
            currentQuote: quote,
            isLoading: false,
          }));
          return quote;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al guardar borrador',
            isLoading: false,
          });
          throw error;
        }
      },

      updateDraft: async (id, data) => {
        try {
          set({ isLoading: true, error: null });
          const quote = await quoteService.updateDraft(id, data);
          set((state) => ({
            quotes: state.quotes.map((q) => (q.id === id ? quote : q)),
            currentQuote: state.currentQuote?.id === id ? quote : state.currentQuote,
            isLoading: false,
          }));
          return quote;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al guardar borrador',
            isLoading: false,
          });
          throw error;
        }
      },

      updateQuote: async (id, data) => {
        try {
          set({ isLoading: true, error: null });
          const quote = await quoteService.updateQuote(id, data);
          set((state) => ({
            quotes: state.quotes.map((q) => (q.id === id ? quote : q)),
            currentQuote: state.currentQuote?.id === id ? quote : state.currentQuote,
            isLoading: false,
          }));
          return quote;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al actualizar cotización',
            isLoading: false,
          });
          throw error;
        }
      },

      deleteQuote: async (id) => {
        try {
          set({ isLoading: true, error: null });
          await quoteService.deleteQuote(id);
          set((state) => ({
            quotes: state.quotes.filter((q) => q.id !== id),
            currentQuote: state.currentQuote?.id === id ? null : state.currentQuote,
            isLoading: false,
          }));
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al eliminar cotización',
            isLoading: false,
          });
          throw error;
        }
      },

      submitQuote: async (id) => {
        try {
          set({ isLoading: true, error: null });
          const quote = await quoteService.submitQuote(id);
          set((state) => ({
            quotes: state.quotes.map((q) => (q.id === id ? quote : q)),
            currentQuote: state.currentQuote?.id === id ? quote : state.currentQuote,
            isLoading: false,
          }));
          return quote;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al enviar cotización',
            isLoading: false,
          });
          throw error;
        }
      },

      setCurrentQuote: (quote) => {
        set({ currentQuote: quote });
      },

      clearError: () => {
        set({ error: null });
      },

      // Templates
      fetchTemplates: async () => {
        try {
          set({ isLoading: true, error: null });
          const templates = await quoteService.getTemplates();
          set({ templates, isLoading: false });
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Error al cargar plantillas',
            isLoading: false,
          });
          throw error;
        }
      },

      saveAsTemplate: async (id, templateName) => {
        try {
          set({ isLoading: true, error: null });
          const template = await quoteService.saveAsTemplate(id, templateName);
          set((state) => ({
            templates: [...state.templates, template],
            isLoading: false,
          }));
          return template;
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Error al guardar plantilla',
            isLoading: false,
          });
          throw error;
        }
      },

      createFromTemplate: async (templateId) => {
        try {
          set({ isLoading: true, error: null });
          const quote = await quoteService.createFromTemplate(templateId);
          set((state) => ({
            quotes: [...state.quotes, quote],
            currentQuote: quote,
            isLoading: false,
          }));
          return quote;
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Error al crear desde plantilla',
            isLoading: false,
          });
          throw error;
        }
      },

      // Tags
      fetchTags: async () => {
        try {
          const tags = await quoteService.getTags();
          set({ tags });
        } catch (error: any) {
          console.error('Error al cargar tags:', error);
        }
      },

      createTag: async (name, color) => {
        try {
          const tag = await quoteService.createTag(name, color);
          set((state) => ({ tags: [...state.tags, tag] }));
          return tag;
        } catch (error: any) {
          set({ error: error.response?.data?.error || 'Error al crear etiqueta' });
          throw error;
        }
      },

      deleteTag: async (id) => {
        try {
          await quoteService.deleteTag(id);
          set((state) => ({ tags: state.tags.filter((t) => t.id !== id) }));
        } catch (error: any) {
          set({ error: error.response?.data?.error || 'Error al eliminar etiqueta' });
          throw error;
        }
      },

      setQuoteTags: async (quoteId, tagIds) => {
        try {
          const tags = await quoteService.setQuoteTags(quoteId, tagIds);
          set((state) => ({
            quotes: state.quotes.map((q) => (q.id === quoteId ? { ...q, tags } : q)),
            currentQuote:
              state.currentQuote?.id === quoteId
                ? { ...state.currentQuote, tags }
                : state.currentQuote,
          }));
        } catch (error: any) {
          set({ error: error.response?.data?.error || 'Error al asignar etiquetas' });
          throw error;
        }
      },

      removeQuoteTag: async (quoteId, tagId) => {
        try {
          await quoteService.removeQuoteTag(quoteId, tagId);
          set((state) => ({
            quotes: state.quotes.map((q) =>
              q.id === quoteId ? { ...q, tags: q.tags?.filter((t) => t.id !== tagId) } : q
            ),
            currentQuote:
              state.currentQuote?.id === quoteId
                ? {
                    ...state.currentQuote,
                    tags: state.currentQuote.tags?.filter((t) => t.id !== tagId),
                  }
                : state.currentQuote,
          }));
        } catch (error: any) {
          set({ error: error.response?.data?.error || 'Error al remover etiqueta' });
          throw error;
        }
      },
    }),
    { name: 'QuoteStore' }
  )
);

export default useQuoteStore;
